<template>
  <div class="testlink">
    <!-- Hard client-side guard: admins only. Even if this component is ever mounted
         outside the admin area, a non-admin sees only this notice and no API is called. -->
    <div v-if="!allowed" class="tl-error"><i class="pi pi-lock"></i> Test link is available to admins only.</div>
    <template v-else>
    <!-- input -->
    <div class="tl-input">
      <InputText v-model="url" placeholder="Paste a tracking / offer link, e.g. http://gm.bf-tracker.com/click?pid=…"
        class="tl-url" @keyup.enter="run" :disabled="resolving" />
      <Button :label="resolving ? 'Resolving…' : 'Run'" icon="pi pi-play" :loading="resolving" @click="run" :disabled="!url.trim()" />
    </div>
    <div class="tl-toggle">
      <ToggleSwitch v-model="previewOnly" inputId="previewOnly" />
      <label for="previewOnly">Preview only <span class="tl-dim">— resolve &amp; show the plan, never send. Turn off to enable the send button.</span></label>
    </div>

    <p v-if="error" class="tl-error"><i class="pi pi-exclamation-triangle"></i> {{ error }}</p>

    <template v-if="result">
      <!-- verdict banner -->
      <div class="tl-verdict" :class="result.isOurs ? 'ok' : 'warn'">
        <i :class="result.isOurs ? 'pi pi-check-circle' : 'pi pi-info-circle'"></i>
        <span v-if="result.isOurs">Resolved to <b>{{ result.matches[0].advertiser }}</b> — matches our link (<b>{{ result.tracker }}</b>).</span>
        <span v-else>Resolved to <b>{{ result.finalHost }}</b> (<b>{{ result.tracker }}</b>) — <b>no matching link</b> in our system for key <code>{{ result.campaignKey || '—' }}</code>. This looks like a third-party link, not one of ours.</span>
      </div>

      <!-- redirect flow -->
      <section class="tl-card">
        <h3>Redirect flow</h3>
        <ol class="tl-flow">
          <li v-for="(h, i) in result.flow" :key="i">
            <span class="tl-via">{{ h.via }}</span>
            <span class="tl-hopurl">{{ h.url }}</span>
          </li>
        </ol>
      </section>

      <!-- detection -->
      <section class="tl-card">
        <h3>Detected</h3>
        <div class="tl-chips">
          <span class="tl-chip">tracker <b>{{ result.tracker }}</b></span>
          <span class="tl-chip" v-if="result.clickId">click id ({{ result.clickIdParam }}) <b>{{ result.clickId }}</b></span>
          <span class="tl-chip" v-if="result.campaignKey">campaign key <b>{{ result.campaignKey }}</b><template v-if="result.keySource"> <span class="tl-dim">({{ result.keySource }})</span></template></span>
          <span class="tl-chip" v-if="result.packedSubid">subid <b>{{ result.packedSubid }}</b></span>
        </div>
      </section>

      <!-- matched link(s) -->
      <section class="tl-card" v-if="result.matches.length">
        <h3>Matched link{{ result.matches.length > 1 ? 's' : '' }}</h3>
        <div v-for="(m, i) in result.matches" :key="i" class="tl-match">
          <div class="tl-match-head">
            <span class="tl-match-name">{{ m.advertiser }} · {{ m.publisher }}</span>
            <span class="tl-salid">salid {{ m.salid }}</span>
            <span class="tl-salid">{{ m.market }}</span>
            <Tag :value="m.status" :severity="m.status === 'linked' ? 'success' : 'warn'" />
            <Tag v-if="m.mapped" value="mapped" severity="secondary" />
            <Tag v-else value="not mapped" severity="warn" />
          </div>
          <div class="tl-chips">
            <span class="tl-chip">key <b>{{ m.internalNote }}</b></span>
          </div>
        </div>
        <div class="tl-expected" v-if="result.expectedPostback">
          <span class="tl-dim">Expected inbound postback:</span>
          <code>{{ result.expectedPostback }}</code>
        </div>
      </section>

      <!-- send -->
      <section class="tl-card tl-send" v-if="result.sendPlan">
        <h3>Send test registration</h3>
        <p class="tl-dim">Fires a real <code>reg</code> through the pb service using the resolved ids, then reads back the result.</p>
        <template v-if="!sendResult">
          <Button label="Open send dialog" icon="pi pi-send" severity="warn"
            :disabled="previewOnly || sending" @click="openSendDialog" />
          <span v-if="previewOnly" class="tl-dim tl-hint">Turn off “Preview only” to enable sending.</span>
        </template>
        <div v-else class="tl-result">
          <div class="tl-chips">
            <span class="tl-chip">pb <b>{{ sendResult.pbStatus }}</b></span>
            <span class="tl-chip" v-if="sendResult.result">advertiser answered <b :class="sendResult.result.advertiserStatus === 200 ? 'ok-txt' : 'bad-txt'">{{ sendResult.result.advertiserStatus ?? '—' }}</b></span>
            <span class="tl-chip" v-if="sendResult.result">attributed <b>{{ sendResult.result.attributed ? 'yes' : 'no' }}</b></span>
          </div>
          <div v-if="sendResult.result" class="tl-chips">
            <span class="tl-chip">{{ sendResult.result.advertiser }} · {{ sendResult.result.publisher }} · {{ sendResult.result.market }}</span>
          </div>
          <div class="tl-expected"><span class="tl-dim">Sent:</span> <code>{{ sendResult.sentUrl }}</code></div>
          <div class="tl-expected" v-if="sendResult.result && sendResult.result.forwardedUrl"><span class="tl-dim">Forwarded:</span> <code>{{ sendResult.result.forwardedUrl }}</code></div>
          <Button label="Reset" text icon="pi pi-replay" @click="sendResult = null" />
        </div>
        <p v-if="sendError" class="tl-error"><i class="pi pi-exclamation-triangle"></i> {{ sendError }}</p>
      </section>
    </template>

    <!-- Armed send dialog. Opens automatically after a successful resolve and
         fires the reg when the bar fills. The countdown IS the confirmation —
         it replaced the old two-step confirm — so Cancel has to stay obvious
         and has to kill the timer, not merely hide the dialog. -->
    <Dialog v-model:visible="sendDialog" modal :closable="!sending" :style="{ width: '34rem' }"
      header="Send test registration" @hide="stopCountdown">
      <template v-if="sendPreview">
        <p class="tl-dim tl-armed-intro">
          This forwards a <b>real</b> registration to <b>{{ sendPreview.advertiser }}</b>.
        </p>

        <div class="tl-kv">
          <div class="tl-kv-row"><span>Click id</span><code class="tl-clickid">{{ sendPreview.clickId }}</code></div>
          <div class="tl-kv-row"><span>Publisher</span><b>{{ sendPreview.publisher }}</b></div>
          <div class="tl-kv-row"><span>Campaign</span><b>{{ sendPreview.campaign }}</b></div>
          <div class="tl-kv-row"><span>Geo</span><b>{{ sendPreview.market }}</b></div>
          <div class="tl-kv-row"><span>Advertiser</span><b>{{ sendPreview.advertiser }}</b> <span class="tl-dim">· salid {{ sendPreview.salid }}</span></div>
        </div>

        <div v-if="!sendResult">
          <button type="button" class="tl-armed" :disabled="sending" @click="fireNow">
            <span class="tl-armed-fill" :style="{ width: countdownPct + '%' }"></span>
            <span class="tl-armed-label">
              <i class="pi pi-send"></i>
              <template v-if="sending">Sending…</template>
              <template v-else>Sending in {{ secondsLeft }}s — click to send now</template>
            </span>
          </button>
          <p class="tl-dim tl-armed-hint">Cancel to stop the countdown.</p>
        </div>
        <div v-else class="tl-chips tl-armed-done">
          <span class="tl-chip">pb <b>{{ sendResult.pbStatus }}</b></span>
          <span class="tl-chip" v-if="sendResult.result">advertiser answered
            <b :class="sendResult.result.advertiserStatus === 200 ? 'ok-txt' : 'bad-txt'">{{ sendResult.result.advertiserStatus ?? '—' }}</b></span>
          <span class="tl-chip" v-if="sendResult.result">attributed <b>{{ sendResult.result.attributed ? 'yes' : 'no' }}</b></span>
        </div>

        <p v-if="sendError" class="tl-error"><i class="pi pi-exclamation-triangle"></i> {{ sendError }}</p>
      </template>

      <template #footer>
        <Button v-if="!sendResult" label="Cancel" text severity="secondary" :disabled="sending" @click="cancelSend" />
        <Button v-else label="Close" text @click="sendDialog = false" />
      </template>
    </Dialog>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import PostbackService from '@/services/PostbackService';

const store = useStore();
// Admins only — belt-and-suspenders on top of the isAdmin backend routes and the
// parent AdminIndex's v-if=isAdmin. This component renders nothing usable and calls
// no endpoint for any other role, so it can never be triggered by a normal account.
const allowed = computed(() => store.getters.isAdmin || store.getters.isSuperAdmin);

const url = ref('');
// Default OFF so a resolve arms the send dialog without a toggle first. The
// guard is no longer a confirm click — it's the 5s countdown plus Cancel. With
// this ON nothing is sent and no dialog opens.
const previewOnly = ref(false);
const resolving = ref(false);
const result = ref(null);
const error = ref('');

const sending = ref(false);
const sendResult = ref(null);
const sendError = ref('');

// ---- armed send ----------------------------------------------------------
// The old flow needed a second click to confirm. That's gone: after a resolve
// we open a dialog and auto-fire once a 5s bar fills, so the countdown is the
// confirmation window. Everything below exists to make that window safe —
// the timer must never outlive the dialog, and it must never fire twice.
const COUNTDOWN_MS = 5000;
const sendDialog = ref(false);
const countdownPct = ref(0);
let countdownTimer = null;

const secondsLeft = computed(() =>
  Math.max(0, Math.ceil(((100 - countdownPct.value) / 100) * (COUNTDOWN_MS / 1000))));

// The four things worth seeing before a real conversion goes out, pulled from
// the resolve payload (primary match — sendPlan only exists when confident).
const sendPreview = computed(() => {
  const r = result.value;
  if (!r || !r.sendPlan) return null;
  const m = (r.matches && r.matches[0]) || {};
  return {
    clickId: r.clickId,
    publisher: m.publisher || '—',
    campaign: m.internalNote || r.campaignKey || '—',
    market: m.market || '—',
    advertiser: m.advertiser || r.finalHost,
    salid: m.salid ?? '—',
  };
});

function stopCountdown() {
  if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
}

function startCountdown() {
  stopCountdown();
  countdownPct.value = 0;
  const started = Date.now();
  countdownTimer = setInterval(() => {
    const pct = Math.min(100, ((Date.now() - started) / COUNTDOWN_MS) * 100);
    countdownPct.value = pct;
    if (pct >= 100) { stopCountdown(); send(); }
  }, 50);
}

function openSendDialog() {
  if (previewOnly.value || !result.value?.sendPlan) return;
  sendError.value = '';
  sendDialog.value = true;
  startCountdown();
}

function cancelSend() { stopCountdown(); sendDialog.value = false; }
function fireNow() { stopCountdown(); send(); }

// A timer that outlives the component would fire a real conversion against a
// screen nobody is looking at.
onUnmounted(stopCountdown);

async function run() {
  if (!allowed.value) return;
  if (!url.value.trim()) return;
  resolving.value = true; error.value = ''; result.value = null;
  sendResult.value = null; sendError.value = '';
  stopCountdown(); sendDialog.value = false;
  try {
    const res = await PostbackService.resolveTestLink(url.value.trim());
    result.value = res.data;
    // Auto-arm only when we actually resolved one of our links. No sendPlan
    // means not ours / ambiguous, and Preview only means never send.
    if (result.value?.sendPlan && !previewOnly.value) openSendDialog();
  } catch (e) {
    error.value = e.response?.data?.message || e.message || 'Failed to resolve the link.';
  } finally {
    resolving.value = false;
  }
}

async function send() {
  if (!allowed.value) return;
  if (!result.value?.sendPlan) return;
  if (sending.value) return;        // the bar finishing and a click must not both fire
  stopCountdown();
  sending.value = true; sendError.value = '';
  try {
    const res = await PostbackService.sendTestReg(result.value.sendPlan);
    sendResult.value = res.data;
  } catch (e) {
    sendError.value = e.response?.data?.message || e.message || 'Send failed.';
  } finally {
    sending.value = false;
  }
}
</script>

<style scoped>
.testlink { margin-top: 1.25rem; max-width: 980px; color: var(--primary-text-color, #d8d8d8); }
.tl-input { display: flex; gap: .6rem; }
.tl-url { flex: 1; }
.tl-toggle { display: flex; align-items: center; gap: .6rem; margin-top: .8rem; font-size: .85rem; }
.tl-dim { opacity: .6; }
.tl-hint { margin-left: .6rem; }
.tl-armed-intro { margin: 0 0 .9rem; font-size: .9rem; }
.tl-kv { display: grid; gap: .4rem; margin-bottom: 1.1rem; font-size: .9rem; }
.tl-kv-row { display: flex; align-items: baseline; gap: .75rem; }
.tl-kv-row > span:first-child { opacity: .6; min-width: 6.5rem; }
.tl-clickid { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; background: rgba(125,125,125,.18); padding: 2px 6px; border-radius: 4px; word-break: break-all; }

/* Button with the countdown rendered inside it. The fill sits behind the label
   and grows to 100% as the timer runs out. */
.tl-armed { position: relative; display: block; width: 100%; overflow: hidden; cursor: pointer;
  border: 1px solid rgba(232,177,74,.55); border-radius: 8px; background: rgba(232,177,74,.12);
  color: inherit; padding: .7rem 1rem; font: inherit; }
.tl-armed:disabled { cursor: default; opacity: .75; }
.tl-armed-fill { position: absolute; inset: 0 auto 0 0; background: rgba(232,177,74,.33);
  transition: width .05s linear; pointer-events: none; }
.tl-armed-label { position: relative; display: flex; align-items: center; justify-content: center; gap: .5rem; font-weight: 600; }
.tl-armed-hint { font-size: .8rem; margin: .5rem 0 0; }
.tl-armed-done { margin-top: .4rem; }
@media (prefers-reduced-motion: reduce) { .tl-armed-fill { transition: none; } }

.tl-error { color: #e5564b; font-size: .85rem; display: flex; align-items: center; gap: .5rem; margin-top: .8rem; }

.tl-verdict { display: flex; align-items: center; gap: .6rem; padding: .7rem 1rem; border-radius: 10px; margin: 1.1rem 0; font-size: .9rem; }
.tl-verdict.ok { background: rgba(58,210,159,.12); border: 1px solid rgba(58,210,159,.35); }
.tl-verdict.warn { background: rgba(232,177,74,.12); border: 1px solid rgba(232,177,74,.35); }
.tl-verdict code { background: rgba(125,125,125,.18); padding: 0 5px; border-radius: 4px; }

.tl-card { border: 1px solid rgba(255,255,255,.12); border-radius: 11px; padding: .9rem 1.1rem; margin-bottom: 1rem; background: rgba(255,255,255,.035); }
.tl-card h3 { margin: 0 0 .7rem; font-size: .82rem; text-transform: uppercase; letter-spacing: .05em; opacity: .7; font-weight: 600; }

.tl-flow { list-style: none; margin: 0; padding: 0; display: grid; gap: .45rem; }
.tl-flow li { display: grid; grid-template-columns: 110px 1fr; gap: .7rem; align-items: baseline; }
.tl-via { font-size: .7rem; font-family: ui-monospace, monospace; opacity: .6; text-align: right; }
.tl-hopurl { font-family: ui-monospace, monospace; font-size: .78rem; word-break: break-all; }

.tl-chips { display: flex; flex-wrap: wrap; gap: .45rem; }
.tl-chip { font-size: .74rem; font-family: ui-monospace, monospace; background: rgba(125,125,125,.14); border: 1px solid rgba(125,125,125,.2); padding: 2px 9px; border-radius: 6px; }
.tl-chip b { font-family: inherit; }

.tl-match { padding: .5rem 0; border-top: 1px solid rgba(125,125,125,.12); }
.tl-match:first-of-type { border-top: 0; }
.tl-match-head { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; margin-bottom: .4rem; }
.tl-match-name { font-weight: 600; }
.tl-salid { font-size: .72rem; opacity: .7; background: rgba(125,125,125,.18); padding: 1px 7px; border-radius: 5px; }
.tl-expected { margin-top: .7rem; font-size: .8rem; display: flex; gap: .5rem; align-items: baseline; flex-wrap: wrap; }
.tl-expected code { font-family: ui-monospace, monospace; font-size: .76rem; background: rgba(125,125,125,.14); padding: 2px 7px; border-radius: 5px; word-break: break-all; }

.tl-send { border-color: rgba(232,177,74,.3); }
.tl-confirm { display: flex; align-items: center; gap: .7rem; flex-wrap: wrap; font-size: .88rem; }
.tl-result { display: grid; gap: .6rem; }
.ok-txt { color: #3ad29f; } .bad-txt { color: #e5564b; }
</style>
